// aiChatClientV5.ts - Client for Snowflake proxy endpoint

import { safeJSONParse, salvageTruncatedJSON } from "../utils/jsonUtils";

/**
 * One entry of the persisted reasoning trace. The snowflake-agent stream emits
 * far more than thinking/status events — tool calls (incl. SQL), SQL execution
 * details (QueryID, rows, state), tool results and errors. We keep every event
 * verbatim (large arrays/strings capped) so the UI can formalise the agent's
 * work after the fact.
 */
export interface TraceEvent {
  /** SSE event name, e.g. "response.status", "response.tool_use". */
  event: string;
  /** sequence_number from the payload when present, else arrival index. */
  seq: number;
  /** Parsed event payload (large arrays/strings capped for persistence). */
  data: any;
  /** True when data was trimmed to bound persistence size. */
  truncated?: boolean;
}

export interface ChatMessageV5 {
  uuid: string | undefined;
  role: "user" | "assistant" | "system";
  content: string;
  rawContent?: string;
  result?: any;
  requestId?: string;
  queryLogId?: string;
  /**
   * True for messages generated client-side (e.g. Quick Actions) rather than by
   * the AI. These are not conversational, so the "Follow up" affordance is hidden
   * (following up on them yields incorrect AI results).
   */
  isLocal?: boolean;
  /** Persisted reasoning text streamed by the snowflake-agent provider. */
  thinkingText?: string;
  /** Persisted step-by-step statuses streamed by the snowflake-agent provider. */
  thinkingStatuses?: { status: string; message: string }[];
  /** Full ordered event trace (steps, tool calls, SQL, results, errors). */
  reasoningTrace?: TraceEvent[];
  metadata?: {
    searchMode: "global" | "current" | "property";
    selectedSpaces?: string[];
    planInfo?: {
      propertyName: string;
      planName?: string;
    };
    isChartMode?: boolean;
    timestamp?: string;
  };
}

export interface SnowflakeProxyResponseData {
  confidence?: number;
  context?: string;
  original_query?: string;
  queries_executed?: number;
  query_intent?: string;
  query_results?: any;
  response?: any; // contains message, exact_matches/partial_matches or recommendations, query_interpretation
  success?: boolean;
  timing_metrics?: any;
  queryLogId?: string;
}

// Hybrid doc+sql relocation response types
export interface RelocationSpaceDetail {
  space_number: string;
  tenant_name?: string;
  mall_name?: string;
  gla?: number;
  lease_end_date?: string | null;
  lease_status?: string | null;
  total_rent?: number | null;
  sales_r12?: number | null;
  area_unit?: string;
  currency?: string;
  highlight_color?: string;
}

export interface RelocationResult {
  tenant_name: string;
  store_record_key?: string;
  store_table_name?: string;
  store_mall_name?: string;
  has_relocation_clause?: boolean;
  section_restriction?: string;
  allowed_space_numbers: string[];
  allowed_spaces_details: RelocationSpaceDetail[];
  supporting_quote?: string;
  source_document_id?: string;
}

export interface RelocationAnalysisSummary {
  stores_analyzed?: number;
  stores_with_clause?: number;
  stores_with_section_restriction?: number;
}

export interface RelocationData {
  format_type: "hybrid_doc_sql.relocation";
  message?: string;
  query_interpretation?: {
    intent?: string;
    document_topic?: string;
  };
  /** Space number of the tenant holding the relocation clause (the source). */
  target_space?: string;
  results: RelocationResult[];
  analysis_summary?: RelocationAnalysisSummary;
}

export interface ChatSuccessResponseV5 {
  success: boolean;
  data: SnowflakeProxyResponseData;
}

export interface StreamCallbacks {
  onThinking?: (text: string) => void;
  onStatus?: (message: string, status: string) => void;
  onTextDelta?: (text: string, contentIndex: number) => void;
  onRequestId?: (requestId: string) => void;
  /** Fired for every parsed SSE event so the full trace can be persisted. */
  onEvent?: (event: string, data: any) => void;
}

export async function callSnowflakeProxy(
  apiUrl: string,
  userPrompt: string,
  planInfo?: { planName?: string; propertyNumber: string; propertyName?: string },
  provider?: "snowflake" | "python" | "snowflake-agent" | "cadviewer-analytics",
  mode?: "Test" | "Prod",
  callbacks?: StreamCallbacks,
  selectedSpaces?: string[],
  isChartMode?: boolean,
  analyticsTarget?: string,
  conversationHistory?: Array<{ role: string; content: string }>,
  followUpContext?: string,
): Promise<ChatSuccessResponseV5> {
  const resolvedProvider = provider || "snowflake";
  const body: {
    userQuery: string;
    provider: "snowflake" | "python" | "snowflake-agent" | "cadviewer-analytics";
    mode: "Test" | "Prod";
    planInfo?: { planName?: string; propertyNumber: string; propertyName?: string };
    selectedSpaces?: string[];
    isChartMode?: boolean;
    analyticsTarget?: string;
    conversationHistory?: Array<{ role: string; content: string }>;
    followUpContext?: string;
  } = {
    userQuery: userPrompt,
    provider: resolvedProvider,
    mode: mode || "Test",
    ...(planInfo && { planInfo }),
    ...(selectedSpaces && selectedSpaces.length > 0 && { selectedSpaces }),
    ...(isChartMode !== undefined && { isChartMode }),
    ...(resolvedProvider === "cadviewer-analytics" && {
      analyticsTarget: analyticsTarget || "DEFAULT",
    }),
    ...(conversationHistory &&
      conversationHistory.length > 0 && { conversationHistory }),
    ...(followUpContext &&
      followUpContext.trim() && { followUpContext }),
  };

  // For streaming providers, use SSE stream
  if (provider === "snowflake-agent") {
    return callSnowflakeProxyStream(apiUrl, body, callbacks);
  }

  // For other providers, use regular JSON response
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let text = await response.text();
    try {
      const j = safeJSONParse(text);
      text = j?.error || text;
    } catch (_) {
      // keep text
    }
    throw new Error(`HTTP ${response.status}: ${text}`);
  }

  const data = (await response.json()) as ChatSuccessResponseV5;
  if (!data || typeof data !== "object") {
    throw new Error(
      "Invalid response structure received from the Snowflake proxy.",
    );
  }
  return data;
}

async function callSnowflakeProxyStream(
  apiUrl: string,
  body: any,
  callbacks?: StreamCallbacks,
): Promise<ChatSuccessResponseV5> {
  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let text = await response.text();
    try {
      const j = safeJSONParse(text);
      text = j?.error || text;
    } catch (_) {
      // keep text
    }
    throw new Error(`HTTP ${response.status}: ${text}`);
  }

  if (!response.body) {
    throw new Error("Response body is null");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent: string | null = null;
  let thinkingText = "";
  let lastThinkingContentIndex: number | null = null;
  let finalResponse: ChatSuccessResponseV5 | null = null;
  const textContent: { [key: number]: string } = {};

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        // Skip empty lines
        if (!line) {
          continue;
        }

        if (line.startsWith("event: ")) {
          currentEvent = line.substring(7).trim();
          continue;
        }

        if (line.startsWith("data: ")) {
          const dataStr = line.substring(6).trim();
          if (dataStr === "[DONE]") {
            currentEvent = null;
            continue;
          }

          // Only process if we have a current event
          if (!currentEvent) {
            continue;
          }

          try {
            const data = JSON.parse(dataStr);

            // Capture every event verbatim for the persisted reasoning trace
            // (tool calls, SQL execution details, tool results, errors, …).
            if (callbacks?.onEvent) {
              callbacks.onEvent(currentEvent, data);
            }

            // Handle request-id event
            if (currentEvent === "request-id") {
              if (data.requestId && callbacks?.onRequestId) {
                callbacks.onRequestId(data.requestId);
              }
            }

            // Handle thinking events
            if (currentEvent === "response.thinking.delta") {
              // Check if this is a new thinking sequence (different content_index)
              const contentIndex = data.content_index;
              if (
                contentIndex !== undefined &&
                contentIndex !== lastThinkingContentIndex
              ) {
                // New thinking sequence, reset
                thinkingText = "";
                lastThinkingContentIndex = contentIndex;
              }
              // Accumulate deltas
              if (data.text !== undefined) {
                thinkingText += data.text;
                if (callbacks?.onThinking) {
                  callbacks.onThinking(thinkingText);
                }
              }
            } else if (currentEvent === "response.thinking") {
              // Replace with complete text (reset first)
              const contentIndex = data.content_index;
              if (contentIndex !== undefined) {
                lastThinkingContentIndex = contentIndex;
              }
              if (data.text !== undefined) {
                thinkingText = data.text;
                if (callbacks?.onThinking) {
                  callbacks.onThinking(thinkingText);
                }
              }
            }

            // Handle status events (response.status)
            if (currentEvent === "response.status") {
              if (data.status && data.message) {
                if (callbacks?.onStatus) {
                  callbacks.onStatus(data.message, data.status);
                }
              }
            }

            // Handle text delta events (response.text.delta)
            if (currentEvent === "response.text.delta") {
              if (data.text !== undefined && data.content_index !== undefined) {
                const contentIndex = data.content_index;
                if (!textContent[contentIndex]) {
                  textContent[contentIndex] = "";
                }
                textContent[contentIndex] += data.text;
                if (callbacks?.onTextDelta) {
                  callbacks.onTextDelta(data.text, contentIndex);
                }
              }
            }

            // Handle final response event (response)
            if (
              currentEvent === "response" &&
              data.content &&
              Array.isArray(data.content)
            ) {
              // Extract text content which contains the JSON response
              const textItem = data.content.find((c: any) => c.type === "text");
              if (textItem && textItem.text) {
                try {
                  // Parse the JSON string in text field (remove comments first)
                  const parsedResponse = safeJSONParse(textItem.text);

                  // Build the complete response structure
                  finalResponse = {
                    success: true,
                    data: {
                      response: parsedResponse,
                    },
                  };
                } catch (parseError) {
                  // The agent's JSON was too damaged for jsonrepair — in
                  // practice, an answer truncated mid-object once it blew the
                  // token budget. Recover the rows that did arrive complete
                  // rather than discarding the whole answer.
                  const salvaged = salvageTruncatedJSON<any>(textItem.text);
                  if (salvaged) {
                    finalResponse = {
                      success: true,
                      data: {
                        response: {
                          ...salvaged,
                          message: salvaged.message
                            ? `${salvaged.message} (Partial answer: the response was cut short, some rows may be missing.)`
                            : "Partial answer: the response was cut short, some rows may be missing.",
                          truncated: true,
                        },
                      },
                    };
                  } else {
                    // Nothing recoverable. Dumping the raw text as the message
                    // used to put a wall of JSON in the chat bubble, so surface a
                    // readable message and keep the payload for the raw view.
                    finalResponse = {
                      success: true,
                      data: {
                        response: {
                          message:
                            "The answer came back incomplete and could not be displayed. Please ask the question again — narrowing it (a specific property, plan or a smaller number of results) usually helps.",
                          malformed: true,
                          rawText: textItem.text,
                        },
                      },
                    };
                  }
                }
              } else {
                // Fallback: construct from accumulated text
                const message = Object.values(textContent).join("") || "";
                finalResponse = {
                  success: true,
                  data: {
                    response: {
                      message,
                    },
                  },
                };
              }
            }
          } catch (e) {
            // Ignore JSON parse errors for malformed data
            console.warn("Failed to parse SSE data:", dataStr, e);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!finalResponse) {
    // If no final response was received, construct one from accumulated text
    const message = Object.values(textContent).join("") || "";
    finalResponse = {
      success: true,
      data: {
        response: {
          message,
        },
      },
    };
  }

  return finalResponse;
}
